/**
 * Tiny mount wrapper that runs `useShouldShowPostPurchaseUpsell` and
 * renders the overlay only when the hook says so. Kept separate from
 * `AppLayout` so the gating hook (and its TanStack queries) doesn't fire
 * on every layout render — it only mounts inside `AppLayout`, which is
 * already scoped to authenticated `/app/*` routes.
 *
 * NOTE on exit animation:
 * `useShouldShowPostPurchaseUpsell` returns `null` the moment the patient
 * dismisses (sets the localStorage flag / dev-dismissed flag). If we
 * also returned null from here, the entire `<PostPurchaseUpsellOverlay>`
 * subtree — including its `<AnimatePresence>` — would unmount immediately
 * and the close slide-down animation would never get to play.
 *
 * We persist the last non-null state in a ref so the overlay stays
 * mounted with `open=false` for one tick while `AnimatePresence` plays
 * the exit, matching how `OverflowDrawer` / `CartDrawer` slide out (CSS
 * transition on an always-mounted element).
 */

import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { PostPurchaseUpsellOverlay } from './PostPurchaseUpsellOverlay';
import {
  useShouldShowPostPurchaseUpsell,
  type PostPurchaseUpsellState,
} from './useShouldShowPostPurchaseUpsell';

export function PostPurchaseUpsellMount() {
  const { pathname } = useLocation();
  const isHome = pathname === '/app' || pathname === '/app/';
  const state = useShouldShowPostPurchaseUpsell();
  // Stash the last open state so the overlay can run its exit animation
  // after the hook returns null. Updated on every render where state is
  // present; preserved otherwise.
  const lastRef = useRef<PostPurchaseUpsellState | null>(null);
  if (state) lastRef.current = state;
  const effective = state ?? lastRef.current;

  if (!effective) return null;
  return (
    <PostPurchaseUpsellOverlay
      continueLabel={isHome ? 'Continue to portal' : 'Continue to intake'}
      labUpsell={effective.labUpsell}
      open={Boolean(state)}
      onClose={effective.close}
      experimentKey={effective.experimentKey}
      variantKey={effective.variantKey}
      recentPurchase={effective.recentPurchase!}
      items={effective.items}
      planUpsell={effective.planUpsell}
      showPlanStep={effective.showPlanStep}
      planExperimentKey={effective.planExperimentKey}
      planVariantKey={effective.planVariantKey}
      dismissPlanStep={effective.dismissPlanStep}
      consultUpsell={effective.consultUpsell}
      showConsultStep={effective.showConsultStep}
      consultExperimentKey={effective.consultExperimentKey}
      consultVariantKey={effective.consultVariantKey}
      dismissConsultStep={effective.dismissConsultStep}
    />
  );
}
